import { Button } from "@/components/ui/button";
import { Ban, Check, CheckCircle2, CreditCard, EyeOff, Loader2, MessageCircle, Package, RefreshCw, Truck } from "lucide-react";

export function OrderStatusActions({
  order,
  isDelivery,
  isFinished,
  deliverers,
  dispatching,
  dispatchPhone,
  setDispatchPhone,
  dispatchName,
  setDispatchName,
  handleAssignDeliverer,
  lastNotification,
  sendWhatsApp,
  setLastNotification,
  showVivaReverify,
  handleReverifyVivaPayment,
  updateStatus,
  handleFinalize,
  nextStatus,
  isPickup,
  isTable,
  cancelling,
  setCancelReason,
  setShowCancelDialog,
  handleQuickDispatch,
}) {
  const isPaid = order.payment_status === "paid" || !!order.paid_at || !!order.payment_confirmed_at;
  return (
    <>
      {lastNotification && (
        <div className="mx-4 mb-2 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between" data-testid="whatsapp-notification-banner">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-green-600" />
            <div><p className="text-sm font-medium text-green-700">Enviar WhatsApp</p><p className="text-xs text-muted-foreground truncate max-w-[280px]">{lastNotification.message}</p></div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" className="bg-green-600 hover:bg-green-700 gap-1" onClick={() => sendWhatsApp(lastNotification)} data-testid="send-whatsapp-btn"><MessageCircle className="w-3.5 h-3.5" />Enviar</Button>
            <Button size="sm" variant="ghost" onClick={() => setLastNotification(null)}><Ban className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      )}

      {showVivaReverify && (
        <div className="p-4 border-t space-y-2 bg-blue-50" data-testid="viva-reverify-section">
          <div className="flex items-center gap-2 text-xs text-blue-700 font-semibold"><CreditCard className="w-3.5 h-3.5 flex-shrink-0" /><span>Pagamento Viva — verificar e enviar ao KDS</span></div>
          <Button className="w-full h-12 text-sm gap-2 font-bold bg-blue-600 hover:bg-blue-700 text-white" onClick={handleReverifyVivaPayment} data-testid="reverify-viva-payment-btn"><RefreshCw className="w-5 h-5" />REVERIFICAR PAGAMENTO VIVA</Button>
        </div>
      )}

      {order.status === "awaiting_payment" && isPaid && (
        <div className="p-4 border-t bg-green-50" data-testid="payment-confirmed-banner">
          <div className="flex items-center gap-2 text-sm text-green-700 font-semibold">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            <span>Pagamento confirmado — pedido a ser processado</span>
          </div>
        </div>
      )}

      {order.status === "awaiting_payment" && !isPaid && (
        <div className="p-4 border-t space-y-2" data-testid="awaiting-payment-section">
          <div className="flex items-center gap-2 text-xs text-orange-600 font-semibold mb-1"><CreditCard className="w-3.5 h-3.5 flex-shrink-0" /><span>Pagamento online a aguardar confirmação</span></div>
          <Button className="w-full h-12 text-sm gap-2 font-bold bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => updateStatus("pending")} data-testid="confirm-payment-btn"><Check className="w-5 h-5" />CONFIRMAR PAGAMENTO RECEBIDO</Button>
          <p className="text-[10px] text-center text-gray-400">Use apenas se o pagamento foi confirmado fora do sistema (ex: Mbway, transferência bancária)</p>
          <Button variant="outline" className="w-full h-10 text-xs gap-2 text-gray-500 border-gray-300 hover:bg-gray-100" onClick={handleFinalize} data-testid="hide-awaiting-payment-btn"><EyeOff className="w-4 h-4" />MOVER PARA OCULTOS</Button>
          <p className="text-[10px] text-center text-gray-400">Use se o pagamento falhou e o pedido não será processado</p>
        </div>
      )}

      {nextStatus && !(order.status === "ready" && isDelivery) && (
        <div className="p-4 border-t space-y-2">
          <Button className={`w-full h-12 text-base gap-2 font-bold ${
            nextStatus === "preparing" ? "bg-blue-600 hover:bg-blue-700" :
              nextStatus === "ready" ? "bg-emerald-600 hover:bg-emerald-700" :
                nextStatus === "delivering" ? "bg-purple-600 hover:bg-purple-700" :
                  nextStatus === "delivered" ? "bg-gray-700 hover:bg-gray-800" : "bg-emerald-600 hover:bg-emerald-700"
          }`} onClick={() => updateStatus(nextStatus)} data-testid="advance-status-btn">
            {nextStatus === "preparing" && <>INICIAR PREPARO</>}
            {nextStatus === "ready" && <><Package className="w-5 h-5" />PRONTO / FEITO</>}
            {nextStatus === "delivering" && <><Truck className="w-5 h-5" />DESPACHAR</>}
            {nextStatus === "delivered" && <><CheckCircle2 className="w-5 h-5" />{isPickup ? "RETIRADO" : isTable ? "SERVIDO" : "ENTREGUE"}</>}
          </Button>
          {nextStatus === "ready" && (
            <p className="text-[10px] text-center text-gray-400">
              {isDelivery ? "Quando o pedido estiver pronto para envio" : isPickup ? "Quando estiver pronto para retirada" : isTable ? "Quando estiver pronto para servir" : ""}
            </p>
          )}
        </div>
      )}

      {order.status === "ready" && isDelivery && !order.deliverer_id && (
        <div className="p-4 border-t bg-purple-50 space-y-3" data-testid="dispatch-section">
          {deliverers.length === 0 ? (
            <>
              <p className="text-sm font-bold text-purple-700 uppercase flex items-center gap-2"><Truck className="w-4 h-4" />Despachar Pedido</p>
              <p className="text-xs text-purple-600">Nenhum entregador cadastrado. Cadastre entregadores em <strong>Equipe &gt; Entregadores</strong> para despachar com 1 clique.</p>
              <div>
                <label className="text-xs font-bold text-gray-600 mb-1 block">DESPACHAR POR WHATSAPP (AVULSO)</label>
                <div className="space-y-2">
                  <input className="h-10 w-full rounded-md border px-3 bg-white" placeholder="Telefone do estafeta..." value={dispatchPhone} onChange={e => setDispatchPhone(e.target.value)} data-testid="dispatch-phone-input" />
                  <input className="h-10 w-full rounded-md border px-3 bg-white" placeholder="Nome (opcional)" value={dispatchName} onChange={e => setDispatchName(e.target.value)} data-testid="dispatch-name-input" />
                </div>
              </div>
              <Button className="w-full h-12 gap-2 bg-purple-600 hover:bg-purple-700 text-base font-bold" onClick={handleAssignDeliverer} disabled={dispatching || !dispatchPhone.trim()} data-testid="dispatch-btn">
                {dispatching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Truck className="w-5 h-5" />}
                DESPACHAR POR WHATSAPP
              </Button>
            </>
          ) : (
            <p className="text-xs text-purple-700 font-medium text-center flex items-center justify-center gap-2"><Truck className="w-4 h-4" />Clique em <strong>DESPACHAR</strong> no topo para selecionar o entregador</p>
          )}
        </div>
      )}

      {isDelivery && !isFinished && order.deliverer_id && (
        <div className="p-4 border-t bg-orange-50 space-y-2" data-testid="resend-estafeta-section">
          <p className="text-xs text-orange-700 font-semibold flex items-center gap-2">
            <Truck className="w-3.5 h-3.5" />Atribuído a {order.deliverer_name || "estafeta"}
          </p>
          <Button className="w-full h-10 text-sm gap-2 bg-orange-600 hover:bg-orange-700 font-bold text-white" disabled={dispatching} onClick={() => handleQuickDispatch(order.deliverer_id, order.deliverer_name)} data-testid="resend-estafeta-btn">
            {dispatching ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            REENVIAR AO ESTAFETA
          </Button>
        </div>
      )}

      {isFinished && !order.hidden && !nextStatus && (
        <div className="p-4 border-t space-y-2">
          {order.status !== "cancelled" && (
            <Button className="w-full h-12 bg-red-600 hover:bg-red-700 text-base gap-2 font-bold text-white" disabled={cancelling} onClick={() => { setCancelReason(""); setShowCancelDialog(true); }} data-testid="cancel-order-bottom-btn">
              {cancelling ? <Loader2 className="w-5 h-5 animate-spin" /> : <Ban className="w-5 h-5" />}
              {cancelling ? "A cancelar..." : "CANCELAR PEDIDO"}
            </Button>
          )}
          {order.status !== "cancelled" && (
            <Button className="w-full h-12 bg-gray-700 hover:bg-gray-800 text-base gap-2 font-bold text-white" onClick={handleFinalize} data-testid="finalize-bottom-btn">
              <EyeOff className="w-5 h-5" />FINALIZAR
            </Button>
          )}
        </div>
      )}
    </>
  );
}
